import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApplicants, getResumeUrl } from '../api';
import { useAuth } from '../contexts/AuthContext';
import type { Applicant } from '../types';

export default function AdminScreening() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [secret, setSecret] = useState('');
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);

  const load = (adminSecret?: string) => {
    setError(null);
    setUnauthorized(false);
    setLoading(true);
    getApplicants(adminSecret)
      .then((r) => setApplicants(r.applicants))
      .catch((e) => {
        const msg = e?.message ?? '';
        setApplicants([]);
        if (msg.includes('401') || msg.includes('Unauthorized')) setUnauthorized(true);
        else setError(msg);
      })
      .finally(() => setLoading(false));
  };

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    if (secret.trim()) load(secret.trim());
  };

  useEffect(() => {
    load((import.meta.env.VITE_ADMIN_SECRET as string) || undefined);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-charcoal">
      <header className="bg-charcoal text-gold border-b-2 border-gold/40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/dashboard" className="text-xl font-semibold text-gold tracking-tight hover:text-gold/80 transition-colors shrink-0">
            The Station
          </Link>
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <span className="text-sm text-gold/90 font-medium shrink-0">Admin · Applicant Screening</span>
            <button
              type="button"
              onClick={() => { logout(); navigate('/login', { replace: true }); }}
              className="text-sm text-gold/80 hover:text-gold transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold text-gold">Applicant screening</h1>
          <Link to="/dashboard" className="text-sm font-medium text-gold hover:text-gold/80 transition-colors shrink-0">
            ← Back to Portal
          </Link>
        </div>
        {unauthorized && (
          <form onSubmit={handleUnlock} className="mb-6 rounded-xl border border-gold/50 bg-gold/10 p-4 max-w-md">
            <label className="block text-sm font-medium text-gold mb-2">Admin secret</label>
            <div className="flex gap-2">
              <input
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                className="flex-1 rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white placeholder-station-muted focus:ring-2 focus:ring-gold"
                placeholder="Enter secret"
              />
              <button type="submit" className="rounded-lg bg-gold text-charcoal font-medium px-4 py-2 hover:bg-gold/90">
                Unlock
              </button>
            </div>
          </form>
        )}
        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/20 border border-red-400/50 text-red-300 text-sm">
            {error}
          </div>
        )}
        {loading && <div className="text-gold/80">Loading applicants…</div>}
        {!loading && applicants.length === 0 && !unauthorized && !error && (
          <div className="rounded-xl border border-leather bg-leather/40 p-8 text-center text-station-muted">
            No applicants yet. Set VITE_ADMIN_SECRET or unlock with the admin secret to view the list.
          </div>
        )}
        {!loading && applicants.length > 0 && (
          <div className="rounded-xl border border-leather bg-leather/40 overflow-x-auto">
            <table className="w-full text-left min-w-[800px]">
              <thead className="bg-leather text-gold/90 text-sm">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Industry</th>
                  <th className="px-4 py-3 font-medium">Gross production</th>
                  <th className="px-4 py-3 font-medium">LinkedIn</th>
                  <th className="px-4 py-3 font-medium">Resume</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-leather/50">
                {applicants.map((a) => (
                  <tr key={a.id} className="hover:bg-leather/20">
                    <td className="px-4 py-3 font-medium text-white">{a.name}</td>
                    <td className="px-4 py-3 text-station-muted">{a.industry}</td>
                    <td className="px-4 py-3 text-gold">${Number(a.gross_production).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {a.linkedin_url ? (
                        <a
                          href={a.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold hover:underline"
                        >
                          Profile
                        </a>
                      ) : (
                        <span className="text-station-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {a.resume_r2_key ? (
                        <a
                          href={getResumeUrl(a.resume_r2_key)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold hover:underline"
                        >
                          View Resume
                        </a>
                      ) : (
                        <span className="text-station-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          a.status === 'approved'
                            ? 'text-green-400'
                            : a.status === 'rejected'
                              ? 'text-red-400'
                              : 'text-amber-400'
                        }
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-station-muted text-sm">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
